import React, { useState, useEffect, useContext, forwardRef } from "react";
import { Controller, useForm } from "react-hook-form";
import { sendLoadingPostRequest } from "../../request/request";
import { Dictionary } from "../../types/Dictionary";
import {
  TextField,
  ThemeProvider,
  Autocomplete,
  createTheme,
  styled,
} from "@mui/material";

import { DictionaryCombo } from "../../types/DictionaryCombo";
import { IStep2FormInput } from "../../types/IStep2FormInput";
import { IIdentificationModel } from "../../types/IIdentificationModel";
import FormContext from "../../contexts/FormContext";
import { Calendar } from "../Calendar/Calendar";
import {
  onlyFirstCapitalize,
  toTitleCase,
  capitalize,
} from "../../helpers/string";
import { LoadingPanel } from "../LoadingPanel/LoadingPanel";
import dayjs from "dayjs";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import localizedFormat from "dayjs/plugin/localizedFormat";
import customParseFormat from "dayjs/plugin/customParseFormat";
import "dayjs/locale/pl";
import { TextMaskCustom } from "../MaskComponent/TextMaskCustom";
import { getBirthDateFromPesel } from "../../helpers/GetFirthDateFromPesel";
import { plPL } from "@mui/x-date-pickers/locales";
import { sanitizeValue } from "../../helpers/inputSanitizer";

dayjs.extend(isSameOrAfter);
dayjs.extend(localizedFormat);
dayjs.extend(customParseFormat);
dayjs.locale("pl");

declare type IdentificationVariant = "identification" | "secondIdentification";

interface IStep2Form {
  setStep: React.Dispatch<React.SetStateAction<number>>;
}

const StyledTextField = styled(TextField)(({ theme }) => ({
  width: "100%",
  "& .MuiOutlinedInput-root .MuiAutocomplete-input:first-child": {
    height: 40,
  },

  "& .MuiInputLabel-root.MuiInputLabel-shrink": {
    transform: "translate(14px, -6px) scale(0.75) !important",
  },
  "& .MuiInputLabel-root": {
    transform: "translate(14px, 20px) scale(1)",
  },
}));

const defaultMaterialTheme = createTheme({
  palette: {
    primary: {
      main: "#000000",
    },
  },
  components: {
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          display: "none",
        },
      },
    },
  },
});

const genders: DictionaryCombo[] = [
  { id: 1, name: "Kobieta" },
  { id: 2, name: "Mężczyzna" },
];

const Step2Form = ({ setStep }: IStep2Form) => {
  const context = useContext(FormContext);

  //const { height, width } = useWindowDimensions();

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    reset,
    getValues,
    setValue,
    clearErrors,
    setError,
    trigger,
  } = useForm<IStep2FormInput>({
    defaultValues: context.step2,
    mode: "onChange",
    shouldFocusError: false,
  });
  const [names, setName] = useState<string[]>([]);
  const [dictionary, setDictionary] = useState<Dictionary>();
  const [loading, setLoading] = useState(false);
  const [disablePesel, setDisablePesel] = useState(false);
  const [disableExpirationDate, setDisableExpirationDate] = useState(false);
  const [showOtherPostalAddress, setShowOtherPostalAddress] = useState(false);
  const [showMatherFater, setShowMatherFather] = useState(false);
  const [checkOtherAddressPostalCode, setCheckOtherAddressPostalCode] =
    useState(false);
  const [checkResident, setCheckResident] = useState(false);
  const [disablePoland, setDisablePoland] = useState<boolean>(false);
  const [poland, setPoland] = useState<DictionaryCombo>();
  const [fileName1, setFileName1] = useState<string>();
  const [fileName2, setFileName2] = useState<string>();
  const [fileName3, setFileName3] = useState<string>();
  const [file1Error, setFile1Error] = useState<string>();
  const [file2Error, setFile2Error] = useState<string>();
  const [file3Error, setFile3Error] = useState<string>();
  const [identityType, setIdentityType] = useState<string>();

  const {
    pesel,
    withoutPesel,
    birthDate,
    gender,
    countryOfBirth,
    address,
    citizenship,
    profession,
    identification,
    otherPostalAddress,
    identificationPhotos,
    homeAddressPhoto,
  } = getValues();

  const file1Input = React.createRef<HTMLInputElement>();
  const file2Input = React.createRef<HTMLInputElement>();
  const file5Input = React.createRef<HTMLInputElement>();

  const inputReference1 = React.createRef<HTMLInputElement>();
  const inputReference2 = React.createRef<HTMLInputElement>();
  const inputReference3 = React.createRef<HTMLInputElement>();

  useEffect(() => {
    window.scrollTo(0, 0);
    dayjs.locale("pl");
    getDictionary();

    if (context.step2) {
      setDisableExpirationDate(
        context.step2.identification.withoutExpirationDate,
      );
      setShowOtherPostalAddress(context.step2.otherPostalAddress);
      if (context.step2.address && context.step2.address.country) {
        setShowMatherFather(
          context.step2.address?.country?.name?.toLocaleLowerCase() !==
          "polska",
        );
      }
      if (context.step2.postalAddress && context.step2.postalAddress.country) {
        setCheckOtherAddressPostalCode(
          context.step2.postalAddress?.country?.name?.toLocaleLowerCase() ===
          "polska",
        );
      }
      if (
        context.step2.identificationPhotos != null &&
        context.step2.identificationPhotos?.mainSide != null
      ) {
        setFileName1(context.step2.identificationPhotos?.mainSide[0]?.name);
      }
      if (
        context.step2.identificationPhotos != null &&
        context.step2.identificationPhotos?.backSide != null
      ) {
        setFileName2(context.step2.identificationPhotos?.backSide[0]?.name);
      }
      if (context.step2.homeAddressPhoto != null) {
        setFileName3(context.step2.homeAddressPhoto[0]?.name);
      }
      setIdentityType(context.step2?.identification?.type?.name ?? undefined);
      if (
        context.step2?.identification?.type?.name?.toLocaleLowerCase() ===
        "paszport"
      ) {
        clearErrors("identificationPhotos.backSide");
        reset({
          ...getValues(),
          identificationPhotos: {
            backSide: undefined,
            mainSide: getValues()?.identificationPhotos?.mainSide,
          },
        });
      }
    }
  }, []);

  function getErrorList(obj: any) {
    const result: string[] = [];

    function parseObjectKeys(obj: any, prefix?: string) {
      for (var prop in obj) {
        var sub = obj[prop];
        if (typeof sub == "object") {
          if (Object.keys(sub).indexOf("message") === -1) {
            parseObjectKeys(sub, prop);
          } else {
            result.push(`${prefix ? prefix + "." : ""}${prop}`);
          }
        }
      }
    }

    parseObjectKeys(obj);

    return result;
  }

  useEffect(() => {
    console.log(errors, "@@@");
    const errorsKeys = getErrorList(errors);
    if (errorsKeys != null) {
      for (let i = 0; i < names.length; i++) {
        const currName = names[i];
        if (errorsKeys.indexOf(currName) > -1) {
          const firstErrorElement = document.getElementsByName(currName)[0];
          firstErrorElement.scrollIntoView({
            behavior: `smooth`,
            block: "center",
          });
          break;
        }
      }
    }
  }, [names, errors]);

  const handleOnSubmit = (event: any) => {
    const form = Array.from(event?.target);
    const names = form
      .map((item: any) => item.name)
      .filter((name: string) => name !== "");
    setName(names);
    handleSubmit(onSubmit)(event);
  };

  const getDictionary = async () => {
    //reloadOptions();
    const { data: response, success } = await sendLoadingPostRequest<
      never,
      Dictionary
    >({
      loadingFunction: setLoading,
      endpoint: "Dictionary/GetAll",
    });
    if (success && response?.data) {
      const poland = response?.data.countries.filter(
        (item) => item?.name?.toLocaleLowerCase() === "polska",
      )[0];
      setPoland(poland);

      setDictionary(response?.data);
    }
  };

  const onSubmit = async (data: IStep2FormInput) => {
    const identity = dictionary?.identityDocumentTypes.find(
      (f) => f.name?.toLowerCase() === "dowód osobisty",
    );
    const showMatherFaterSection =
      data?.address?.country?.name?.toLocaleLowerCase() !== "polska";
    const { data: confirmResponse, success } = await sendLoadingPostRequest<
      any,
      string
    >({
      loadingFunction: setLoading,
      endpoint: "Agreement/Step2",
      params: {
        ...data,
        identification: {
          ...data.identification,
          type: identity,
        },
        identificationPhotos: {
          mainSide: await getBase64(identificationPhotos.mainSide),
          backSide: await getBase64(identificationPhotos.backSide),
        },
        homeAddressPhoto: await getBase64(homeAddressPhoto),
        address: {
          ...data.address,
          fatherName: showMatherFaterSection ? data?.address?.fatherName : "",
          motherName: showMatherFaterSection ? data?.address?.motherName : "",
        },
      },
    });

    if (confirmResponse?.data) {
      if (context.setStep2) context.setStep2(data);
      if (context.setMObywatelGuid)
        context.setMObywatelGuid(confirmResponse.data);
      setStep(9);
    }
  };

  const setPostalAddressCheckbox = () => {
    const { otherPostalAddress } = getValues();
    setValue("otherPostalAddress", !otherPostalAddress);
    if (otherPostalAddress) {
      clearErrors("postalAddress.country");
      clearErrors("postalAddress.postCode");
      clearErrors("postalAddress.city");
      clearErrors("postalAddress.street");
      clearErrors("postalAddress.house");
      clearErrors("postalAddress.apartment");
      reset({
        ...getValues(),
        postalAddress: undefined,
      });
    } else {
      reset({
        ...getValues(),
        postalAddress: {
          country: poland,
        },
      });
      setCheckOtherAddressPostalCode(true);
    }
    setShowOtherPostalAddress(!otherPostalAddress);
  };

  const handleExpirationDate = () => {
    const withoutExpirationDate = getValues(
      "identification.withoutExpirationDate",
    );
    if (!withoutExpirationDate) {
      clearErrors("identification.expirationDate");
      setValue("identification.expirationDate", undefined);
    }
    setValue("identification.withoutExpirationDate", !withoutExpirationDate);
    setDisableExpirationDate(!withoutExpirationDate);
  };

  const addYearToExpirationDate = () => {
    const { identification } = getValues();
    const withoutExpirationDate = getValues(
      "identification.withoutExpirationDate",
    );
    if (
      identification.type?.name === "Dowód osobisty" &&
      identification.releaseDate &&
      !withoutExpirationDate
    ) {
      setValue(
        "identification.expirationDate",
        dayjs(identification.releaseDate).add(10, "years").format("YYYY-MM-DD"),
        {
          shouldValidate: true,
        },
      );
    }
  };

  const getAddressCountries = () => {
    const countries = dictionary?.countries.reduce(polandFirst, []) ?? [];
    if (disablePoland) {
      return countries.filter(
        (item) => item?.name?.toLocaleLowerCase() !== "polska",
      );
    }
    return countries;
  };

  const IdentityTab = (
    identificationVariant: IdentificationVariant,
    identification: IIdentificationModel,
  ) => {
    return (
      <>
        <div className="row px-3 mt-4">
          <div className="form-group mt-1 mb-1">
            <Controller
              render={({ field }) => (
                <StyledTextField
                  ref={field.ref}
                  label="Seria i numer"
                  name="identification.seriesAndNumber"
                  variant="outlined"
                  value={field.value}
                  error={errors.identification?.seriesAndNumber != null}
                  onChange={(event: any) => {
                    field.onChange(event.target.value);
                    addYearToExpirationDate();
                  }}
                  onBlur={(event: any) => {
                    if (event.target.value)
                      field.onChange(capitalize(event.target.value));
                  }}
                  inputProps={{
                    maxLength: 20,
                  }}
                />
              )}
              name="identification.seriesAndNumber"
              control={control}
              rules={{
                required: "Seria i numer jest wymagana",
                validate: (value) =>
                  validateIdentityDocumentNumber(value, true, 3, 6),
                maxLength: {
                  value: 20,
                  message: "Seria i numer są za długie",
                },
              }}
            />
            {errors.identification?.seriesAndNumber &&
              errors.identification?.seriesAndNumber.type === "validate" && (
                <span className="validationError" role="alert">
                  <i className="fa fa-exclamation-circle" />
                  Nieprawidłowy Numer dowodu
                </span>
              )}
            {errors.identification?.seriesAndNumber &&
              errors.identification?.seriesAndNumber.type !== "validate" && (
                <span className="validationError" role="alert">
                  <i className="fa fa-exclamation-circle" />
                  {errors.identification.seriesAndNumber.message}
                </span>
              )}
          </div>
        </div>

        <div className="row px-3 mt-4">
          <div className="form-group mt-1 mb-1">
            <Controller
              render={({ field }) => (
                <Calendar
                  localeText={
                    plPL.components.MuiLocalizationProvider.defaultProps
                      .localeText
                  }
                  ref={field.ref}
                  disableFuture
                  format="YYYY-MM-DD"
                  label="Data wydania"
                  value={field.value ? dayjs(field.value) : null}
                  onChange={(date: any) => {
                    setValue(
                      "identification.releaseDate",
                      date ? dayjs(date).format("YYYY-MM-DD") : undefined,
                      {
                        shouldValidate: true,
                      },
                    );
                    addYearToExpirationDate();
                  }}
                  renderInput={(props: any) => (
                    <TextField
                      {...props}
                      error={errors.identification?.releaseDate != null}
                      name="identification.releaseDate"
                      fullWidth
                      variant="outlined"
                    />
                  )}
                />
              )}
              name="identification.releaseDate"
              control={control}
              rules={{
                required: "Data wydania jest wymagana",
                validate: {
                  minDate: (value) => {
                    return dayjs(value).isBefore(dayjs());
                  },
                },
              }}
            />
            {errors.identification?.releaseDate &&
              errors.identification?.releaseDate.type === "required" && (
                <span className="validationError" role="alert">
                  <i className="fa fa-exclamation-circle" />
                  {errors.identification.releaseDate.message}
                </span>
              )}
            {errors.identification?.releaseDate &&
              errors.identification?.releaseDate.type !== "required" && (
                <span className="validationError" role="alert">
                  <i className="fa fa-exclamation-circle" />
                  Podano błędną datę
                </span>
              )}
          </div>
        </div>

        <div className="row px-3 mt-4">
          <div className="form-group mt-1 mb-1">
            <Controller
              render={({ field }) => (
                <Calendar
                  localeText={
                    plPL.components.MuiLocalizationProvider.defaultProps
                      .localeText
                  }
                  ref={field.ref}
                  disabled={disableExpirationDate}
                  disablePast
                  format="YYYY-MM-DD"
                  label="Data ważności"
                  value={field.value ? dayjs(field.value) : null}
                  onChange={(date: any) => {
                    setValue(
                      "identification.expirationDate",
                      date ? dayjs(date).format("YYYY-MM-DD") : undefined,
                      {
                        shouldValidate: true,
                      },
                    );
                  }}
                  renderInput={(props: any) => (
                    <TextField
                      {...props}
                      error={errors.identification?.expirationDate != null}
                      name="identification.expirationDate"
                      fullWidth
                      variant="outlined"
                    />
                  )}
                />
              )}
              name="identification.expirationDate"
              control={control}
              rules={{
                required: !disableExpirationDate
                  ? "Data ważności jest wymagana"
                  : undefined,
                validate: {
                  minDate: (value) => {
                    return dayjs(value).isSameOrAfter(dayjs());
                  },
                },
              }}
            />
            {errors.identification?.expirationDate &&
              errors.identification?.expirationDate.type === "required" && (
                <span className="validationError" role="alert">
                  <i className="fa fa-exclamation-circle" />
                  {errors.identification.expirationDate.message}
                </span>
              )}
            {errors.identification?.expirationDate &&
              errors.identification?.expirationDate.type !== "required" && (
                <span className="validationError" role="alert">
                  <i className="fa fa-exclamation-circle" />
                  Podano błędną datę
                </span>
              )}
          </div>
        </div>

        <div className="row px-3 mt-1 mb-1">
          <div
            onClick={handleExpirationDate}
            className="custom-control custom-checkbox"
          >
            <input
              {...register("identification.withoutExpirationDate")}
              id="identificationWithoutExpirationDate"
              type="checkbox"
              className="custom-control-input"
            />
            <label className="custom-control-label">Brak daty ważności</label>
          </div>
        </div>

        <div className="row px-3 mt-4">
          <div className="form-group mt-1 mb-1">
            <Controller
              render={({ field }) => (
                <>
                  <Autocomplete
                    ref={field.ref}
                    disabled={true}
                    options={
                      dictionary?.countries.reduce(polandFirst, []) ?? []
                    }
                    value={field.value}
                    onChange={(
                      event: any,
                      newValue: DictionaryCombo | null,
                    ) => {
                      field.onChange(newValue);
                    }}
                    getOptionLabel={(option) => option.name}
                    isOptionEqualToValue={(option, value) =>
                      option.id === value.id
                    }
                    renderInput={(params) => (
                      <StyledTextField
                        {...params}
                        error={errors.identification?.issuingCountry != null}
                        name="identification.issuingCountry"
                        label="Kraj wydania"
                        variant="outlined"
                        size="small"
                      />
                    )}
                    noOptionsText={"Brak danych"}
                  />
                </>
              )}
              name="identification.issuingCountry"
              control={control}
              rules={{
                required: "Kraj wydania jest wymagany",
              }}
            />
            {errors.identification?.issuingCountry && (
              <span className="validationError" role="alert">
                <i className="fa fa-exclamation-circle" />
                Kraj wydania jest wymagany
              </span>
            )}
          </div>
        </div>
      </>
    );
  };

  const AddressTab = () => {
    return (
      <>
        <div className="row px-3">
          <div className="form-group mt-2 mb-1">
            <Controller
              render={({ field }) => (
                <>
                  <Autocomplete
                    ref={field.ref}
                    options={getAddressCountries()}
                    value={field.value}
                    onChange={(
                      event: any,
                      newValue: DictionaryCombo | null,
                    ) => {
                      field.onChange(newValue);
                      setShowMatherFather(
                        newValue?.name?.toLocaleLowerCase() !== "polska",
                      );
                      if (newValue?.name?.toLocaleLowerCase() === "polska") {
                        clearErrors("address.motherName");
                        clearErrors("address.fatherName");
                        setValue("address.motherName", "");
                        setValue("address.fatherName", "");
                      } else {
                        clearErrors("address.postCode");
                      }
                      checkIsResident();
                    }}
                    getOptionLabel={(option) => option.name}
                    isOptionEqualToValue={(option, value) =>
                      option?.id === value?.id
                    }
                    renderInput={(params) => (
                      <StyledTextField
                        {...params}
                        error={errors.address?.country != null}
                        name="address.country"
                        label="Kraj zamieszkania"
                        variant="outlined"
                        size="small"
                      />
                    )}
                    noOptionsText={"Brak danych"}
                  />
                </>
              )}
              name="address.country"
              control={control}
              rules={{
                required: "Kraj zamieszkania jest wymagany",
              }}
            />
            {errors.address?.country && (
              <span className="validationError" role="alert">
                <i className="fa fa-exclamation-circle" />
                Kraj jest wymagany
              </span>
            )}
          </div>
        </div>
        <div className={`${showMatherFater ? "" : "confirmEmailHide"}`}>
          <div className="row px-3 mt-4">
            <div className="form-group mt-1 mb-1">
              <Controller
                render={({ field }) => (
                  <StyledTextField
                    ref={field.ref}
                    label="Imię ojca"
                    name="address.fatherName"
                    variant="outlined"
                    value={field.value}
                    onChange={(event: any) => {
                      field.onChange(event.target.value);
                    }}
                    onBlur={(event: any) => {
                      if (event.target.value)
                        field.onChange(onlyFirstCapitalize(event.target.value));
                    }}
                    error={errors.address?.fatherName != null}
                    inputProps={{
                      maxLength: 200,
                    }}
                  />
                )}
                name="address.fatherName"
                control={control}
                rules={{
                  required: showMatherFater
                    ? "Imię ojca jest wymagane"
                    : undefined,
                  minLength: {
                    value: 2,
                    message: "Imię ojca jest za krótkie",
                  },
                  maxLength: {
                    value: 200,
                    message: "Imię ojca jest za długie",
                  },
                  validate: {
                    invalidChars: (value) =>
                      !value ||
                      /^[a-zA-ZąćęłńóśźżĆĄĘŁŃÓŚŹŻ\s'-]+$/.test(value) ||
                      "Imię ojca zawiera niedozwolone znaki",
                  },
                }}
              />
              {errors.address?.fatherName && (
                <span className="validationError" role="alert">
                  <i className="fa fa-exclamation-circle" />
                  {errors.address.fatherName.message}
                </span>
              )}
            </div>
          </div>
          <div className="row px-3 mt-4 mb-4">
            <div className="form-group mt-1 mb-1">
              <Controller
                render={({ field }) => (
                  <StyledTextField
                    ref={field.ref}
                    label="Imię matki"
                    name="address.motherName"
                    variant="outlined"
                    value={field.value}
                    onChange={(event: any) => {
                      field.onChange(event.target.value);
                    }}
                    onBlur={(event: any) => {
                      if (event.target.value)
                        field.onChange(onlyFirstCapitalize(event.target.value));
                    }}
                    error={errors.address?.motherName != null}
                    inputProps={{
                      maxLength: 200,
                    }}
                  />
                )}
                name="address.motherName"
                control={control}
                rules={{
                  required: showMatherFater
                    ? "Imię matki jest wymagane"
                    : undefined,
                  minLength: {
                    value: 2,
                    message: "Imię matki jest za krótkie",
                  },
                  maxLength: {
                    value: 200,
                    message: "Imię matki jest za długie",
                  },
                  validate: {
                    invalidChars: (value) =>
                      !value ||
                      /^[a-zA-ZąćęłńóśźżĆĄĘŁŃÓŚŹŻ\s'-]+$/.test(value) ||
                      "Imię matki zawiera niedozwolone znaki",
                  },
                }}
              />
              {errors.address?.motherName && (
                <span className="validationError" role="alert">
                  <i className="fa fa-exclamation-circle" />
                  {errors.address.motherName.message}
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="row px-3">
          <div className="form-group mt-1 mb-1">
            <Controller
              render={({ field }) =>
                !showMatherFater ? (
                  <StyledTextField
                    ref={field.ref}
                    label="Kod pocztowy"
                    name="address.postCode"
                    variant="outlined"
                    error={Boolean(errors.address?.postCode)}
                    inputProps={{
                      maxLength: 9,
                    }}
                    InputProps={{
                      inputProps: {
                        name: "address.postCode",
                        value: field.value ?? "",
                        onBlur: field.onBlur,
                        onChange: field.onChange,
                        disableUnderline: true,
                        maskValue: "00-000",
                      },
                      inputComponent: TextMaskCustom as any,
                    }}
                  />
                ) : (
                  <StyledTextField
                    ref={field.ref}
                    value={field.value}
                    onChange={(event: any) => {
                      field.onChange(event.target.value);
                    }}
                    label="Kod pocztowy"
                    name="address.postCode"
                    variant="outlined"
                    error={errors.address?.postCode != null}
                    inputProps={{
                      maxLength: 9,
                    }}
                  />
                )
              }
              name="address.postCode"
              control={control}
              rules={{
                required: "Kod pocztowy jest wymagany",
                maxLength: {
                  value: 9,
                  message: "Kod pocztowy jest za długi",
                },
                pattern: !showMatherFater
                  ? {
                    value: /\b[0-9][0-9]-[0-9][0-9][0-9]\b/,
                    message: "Nieprawidłowy kod pocztowy",
                  }
                  : undefined,
              }}
            />
            {errors.address?.postCode && (
              <span className="validationError" role="alert">
                <i className="fa fa-exclamation-circle" />
                {errors.address.postCode.message}
              </span>
            )}
          </div>
        </div>

        <div className="row px-3 mt-4">
          <div className="form-group mt-1 mb-1">
            <Controller
              render={({ field }) => (
                <StyledTextField
                  ref={field.ref}
                  label="Miejscowość"
                  name="address.city"
                  variant="outlined"
                  value={field.value}
                  error={errors.address?.city != null}
                  onChange={(event: any) => {
                    field.onChange(event.target.value);
                  }}
                  onBlur={(event: any) => {
                    if (event.target.value)
                      field.onChange(toTitleCase(event.target.value));
                  }}
                  inputProps={{
                    maxLength: 200,
                  }}
                />
              )}
              name="address.city"
              control={control}
              rules={{
                required: "Miejscowość jest wymagana",
                maxLength: {
                  value: 200,
                  message: "Miejscowość jest za długa",
                },
                validate: {
                  invalidChars: (value) => {
                    const { invalidChars } = sanitizeValue(value);
                    if (invalidChars.length === 0) return true;

                    const unique = Array.from(new Set(invalidChars));
                    return `Podano nieprawidłowe znaki: ${unique.join(" ")}`;
                  },
                },
              }}
            />
            {errors.address?.city && (
              <span className="validationError" role="alert">
                <i className="fa fa-exclamation-circle" />
                {errors.address.city.message}
              </span>
            )}
          </div>
        </div>

        <div className="row px-3 mt-4">
          <div className="form-group mt-1 mb-1">
            <Controller
              render={({ field }) => (
                <StyledTextField
                  ref={field.ref}
                  label="Ulica"
                  name="address.street"
                  variant="outlined"
                  value={field.value}
                  onChange={(event: any) => {
                    field.onChange(event.target.value);
                  }}
                  onBlur={(event: any) => {
                    if (event.target.value)
                      field.onChange(toTitleCase(event.target.value));
                  }}
                  error={errors.address?.street != null}
                  inputProps={{
                    maxLength: 200,
                  }}
                />
              )}
              name="address.street"
              control={control}
              rules={{
                maxLength: {
                  value: 200,
                  message: "Ulica jest za długa",
                },
                validate: {
                  invalidChars: (value) => {
                    const { invalidChars } = sanitizeValue(value);
                    if (invalidChars.length === 0) return true;

                    const unique = Array.from(new Set(invalidChars));
                    return `Podano nieprawidłowe znaki: ${unique.join(" ")}`;
                  },
                },
              }}
            />
            {errors.address?.street && (
              <span className="validationError" role="alert">
                <i className="fa fa-exclamation-circle" />
                {errors.address.street.message}
              </span>
            )}
          </div>
        </div>

        <div className="row px-3 mt-4">
          <div className="form-group mt-1 mb-1">
            <Controller
              render={({ field }) => (
                <StyledTextField
                  ref={field.ref}
                  label="Numer domu"
                  name="address.house"
                  variant="outlined"
                  value={field.value}
                  onChange={(event: any) => {
                    field.onChange(event.target.value);
                  }}
                  error={errors.address?.house != null}
                  inputProps={{
                    maxLength: 10,
                  }}
                />
              )}
              name="address.house"
              control={control}
              rules={{
                required: "Numer domu jest wymagany",
                maxLength: {
                  value: 10,
                  message: "Numer domu jest za długi",
                },
                validate: {
                  invalidChars: (value) => {
                    const { invalidChars } = sanitizeValue(value);
                    if (invalidChars.length === 0) return true;

                    const unique = Array.from(new Set(invalidChars));
                    return `Podano nieprawidłowe znaki: ${unique.join(" ")}`;
                  },
                },
              }}
            />
            {errors.address?.house && (
              <span className="validationError" role="alert">
                <i className="fa fa-exclamation-circle" />
                {errors.address.house.message}
              </span>
            )}
          </div>
        </div>

        <div className="row px-3 mt-4">
          <div className="form-group mt-1 mb-2">
            <Controller
              render={({ field }) => (
                <StyledTextField
                  ref={field.ref}
                  label="Numer mieszkania"
                  name="address.apartment"
                  variant="outlined"
                  error={errors.address?.apartment != null}
                  value={field.value}
                  onChange={(event: any) => {
                    field.onChange(event.target.value);
                  }}
                  inputProps={{
                    maxLength: 10,
                  }}
                />
              )}
              name="address.apartment"
              control={control}
              rules={{
                maxLength: {
                  value: 10,
                  message: "Numer mieszkania jest za długi",
                },
                validate: {
                  invalidChars: (value) => {
                    const { invalidChars } = sanitizeValue(value);
                    if (invalidChars.length === 0) return true;

                    const unique = Array.from(new Set(invalidChars));
                    return `Podano nieprawidłowe znaki: ${unique.join(" ")}`;
                  },
                },
              }}
            />
            {errors.address?.apartment && (
              <span className="validationError" role="alert">
                <i className="fa fa-exclamation-circle" />
                {errors.address.apartment.message}
              </span>
            )}
          </div>
        </div>
      </>
    );
  };

  const getBase64 = async (file: any) => {
    if (file && file.length > 0) {
      var reader = new FileReader();
      reader.readAsDataURL(file[0]);
      return new Promise((resolve) => {
        reader.onload = () => {
          resolve(reader.result);
        };
      });
    }
    return "";
  };

  const compareDictionary = (a: DictionaryCombo, b: DictionaryCombo) => {
    if (a.name < b.name) {
      return -1;
    }
    if (a.name > b.name) {
      return 1;
    }
    return 0;
  };

  const polandFirst = (acc: DictionaryCombo[], element: DictionaryCombo) => {
    if (element.name?.toLocaleLowerCase() === "polska") {
      return [element, ...acc];
    }
    return [...acc, element];
  };

  function validateIdentityDocumentNumber(
    number: string | undefined,
    fromPoland: boolean,
    numberOfLetters: number,
    numberOfDigits: number,
  ): boolean {
    if (!number) {
      return false;
    }

    if (!fromPoland) {
      return true;
    }

    if (number.length != numberOfLetters + numberOfDigits) {
      return false;
    }

    const letterValues = "0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ";
    const weights = [7, 3, 1];

    let sum = 0;
    for (let i = 0; i < numberOfLetters; i++) {
      const letterValueIndex = letterValues.indexOf(number[i]);
      if (letterValueIndex < 10) {
        return false;
      }

      sum += letterValueIndex * weights[i % 3];
    }

    const digitsStartIndexWithoutCheckSum = numberOfLetters + 1;
    const numberOfDigitsWithoutCheckSum = numberOfDigits - 1;
    for (let i = 0; i < numberOfDigitsWithoutCheckSum; i++) {
      const letterValueIndex = letterValues.indexOf(
        number[i + digitsStartIndexWithoutCheckSum],
      );
      if (letterValueIndex < 0 || letterValueIndex > 9) {
        return false;
      }

      sum += letterValueIndex * weights[(i + numberOfLetters) % 3];
    }

    const checkSum = sum % 10;

    return checkSum == letterValues.indexOf(number[numberOfLetters]);
  }

  const checkPesel = (pesel: string) => {
    // 1. Sprawdzenie długości i wyłącznie cyfr
    if (!/^\d{11}$/.test(pesel)) {
      return false;
    }

    // 2. Sprawdzanie miesiąca (2-3 cyfra) i dnia (4-5 cyfra)
    const monthWithCentury = Number(pesel.substring(2, 4));
    if (!monthWithCentury || monthWithCentury % 20 > 12) {
      return false;
    }
    const day = Number(pesel.substring(4, 6));
    if (day < 1 || day > 31) {
      return false;
    }

    // 3. Walidacja sumy kontrolnej PESEL
    const weights = [1, 3, 7, 9]; // wagi cyklicznie powtarzane
    const digits = pesel.split("").map((digit) => parseInt(digit, 10));
    const controlDigit = digits[10];
    digits.pop(); // usuwa ostatnią cyfrę (kontrolną)

    const controlSum =
      digits.reduce((acc, curr, idx) => {
        return acc + curr * weights[idx % 4];
      }, 0) % 10;

    const isChecksumValid =
      10 - (controlSum === 0 ? 10 : controlSum) === controlDigit;
    if (!isChecksumValid) {
      return false;
    }

    // 4. Wyliczenie faktycznej daty urodzenia (dayjs)
    const birthDateStr = getBirthDateFromPesel(pesel);
    // zakładamy, że getBirthDateFromPesel zwraca np. "YYYY-MM-DD"
    const birthDate = dayjs(birthDateStr);

    // 5. Sprawdzenie, czy osoba ma >= 18 lat
    // Porównujemy różnicę w latach z dokładnością (opcjonalnie 'year', 'month', 'day' - w tym przypadku 'year' wystarczy).
    if (dayjs().diff(birthDate, "year", true) < 18) {
      return false;
    }

    // 6. (Opcjonalne) sprawdzenie, czy nie jest starsza niż 105 lat
    if (dayjs().diff(birthDate, "year", true) > 105) {
      return false;
    }

    // 7. Jeśli wszystko jest ok, ustawiamy stan w komponencie i zwracamy true
    setValue("birthDate", birthDateStr, {
      shouldValidate: true,
    });

    const gender = Number(pesel[9]) % 2 === 1 ? genders[1] : genders[0];
    setValue("gender", gender, {
      shouldValidate: true,
    });
    return true;
  };

  function FileListItems(files: any) {
    if (files && files[0]) {
      if (files[0].size === 0) return "Plik jest za mały.";
      const kbSize = files[0].size / 1024;
      const mbSize = kbSize / 1024;

      if (mbSize > 4) {
        return "Plik jest za duży";
      }

      const mimeType = files[0].type;
      const name = files[0].name;
      if (
        mimeType === "image/png" ||
        mimeType === "image/jpeg" ||
        mimeType === "application/pdf"
      ) {
        const b = new ClipboardEvent("").clipboardData || new DataTransfer();
        for (let i = 0, len = files.length; i < len; i++) b.items.add(files[i]);
        return b.files;
      }
      return "Nieobsługiwany format (obsługiwane formaty: .jpg .png .pdf)";
    }
    return undefined;
  }

  const checkIsResident = () => { };

  return (
    <>
      <ThemeProvider theme={defaultMaterialTheme}>
        <form
          onKeyPress={(e) => {
            e.key === "Enter" && e.preventDefault();
          }}
          onSubmit={handleOnSubmit}
        >
          <hr />

          <p className="bold red section">Dane osobowe</p>
          <div className="row px-3">
            <div className="form-group mt-2 mb-1">
              <Controller
                render={({ field }) => (
                  <StyledTextField
                    ref={field.ref}
                    disabled={disablePesel}
                    label="PESEL"
                    name="pesel"
                    variant="outlined"
                    error={errors.pesel != null}
                    value={field.value}
                    onChange={(event: any) => {
                      field.onChange(event.target.value);
                      checkPesel(event.target.value);
                      setCheckResident(false);
                    }}
                    inputProps={{
                      maxLength: 11,
                    }}
                  />
                )}
                name="pesel"
                control={control}
                rules={{
                  required: !disablePesel ? "PESEL jest wymagany" : undefined,
                  validate: (value) => disablePesel || checkPesel(value),
                }}
              />
              {errors.pesel && errors.pesel.type === "validate" && (
                <span className="validationError" role="alert">
                  <i className="fa fa-exclamation-circle" />
                  Nieprawidłowy PESEL
                </span>
              )}
              {checkResident ? (
                <p className="validationError" role="alert">
                  <i className="fa fa-exclamation-circle" />W przypadku
                  rezydentów wymagany numer PESEL
                </p>
              ) : errors.pesel && errors.pesel.type !== "validate" ? (
                <span className="validationError" role="alert">
                  <i className="fa fa-exclamation-circle" />
                  {errors.pesel.message ?? "Nieprawidłowy PESEL"}
                </span>
              ) : null}
            </div>
          </div>

          <div className="row px-3 mt-4">
            <div className="form-group mt-1 mb-1">
              <Controller
                render={({ field }) => (
                  <Calendar
                    localeText={
                      plPL.components.MuiLocalizationProvider.defaultProps
                        .localeText
                    }
                    ref={field.ref}
                    disableFuture
                    format="YYYY-MM-DD"
                    minDate={dayjs().subtract(105, "years")}
                    maxDate={dayjs().subtract(18, "years")}
                    label="Data urodzenia"
                    value={field.value ? dayjs(field.value) : null}
                    onChange={(date: any) => {
                      setValue(
                        "birthDate",
                        date ? dayjs(date).format("YYYY-MM-DD") : undefined,
                        {
                          shouldValidate: true,
                        },
                      );
                      setValue("pesel", "", {
                        shouldValidate: true,
                      });
                      setValue("gender", undefined, {
                        shouldValidate: true,
                      });
                      checkIsResident();
                    }}
                    renderInput={(props: any) => (
                      <TextField
                        {...props}
                        error={errors.birthDate != null}
                        name="birthDate"
                        fullWidth
                        variant="outlined"
                      />
                    )}
                  />
                )}
                name="birthDate"
                control={control}
                rules={{
                  required: "Data urodzenia jest wymagana",
                  validate: {
                    minDate: (value) => {
                      return dayjs(value, "YYYY-MM-DD").isBefore(
                        dayjs().subtract(18, "years"),
                      );
                    },
                    maxDate: (value) => {
                      return dayjs(value, "YYYY-MM-DD").isAfter(
                        dayjs().subtract(105, "years"),
                      );
                    },
                  },
                }}
              />
              {errors.birthDate && errors.birthDate.type === "required" && (
                <span className="validationError" role="alert">
                  <i className="fa fa-exclamation-circle" />
                  {errors.birthDate.message}
                </span>
              )}
              {errors.birthDate && errors.birthDate.type !== "required" && (
                <span className="validationError" role="alert">
                  <i className="fa fa-exclamation-circle" />
                  Podano błędną datę
                </span>
              )}
            </div>
          </div>

          <div className="row px-3 mt-4">
            <div className="form-group mt-1 mb-1">
              <Controller
                render={({ field }) => (
                  <StyledTextField
                    ref={field.ref}
                    label="Drugie imię"
                    name="secondName"
                    variant="outlined"
                    value={field.value}
                    error={errors.secondName != null}
                    onChange={(event: any) => {
                      field.onChange(event.target.value);
                    }}
                    onBlur={(event: any) => {
                      if (event.target.value)
                        field.onChange(onlyFirstCapitalize(event.target.value));
                    }}
                    inputProps={{
                      maxLength: 50,
                    }}
                  />
                )}
                name="secondName"
                control={control}
                rules={{
                  minLength: {
                    value: 2,
                    message: "Imię jest za krótkie",
                  },
                  maxLength: {
                    value: 50,
                    message: "Imię jest za długie",
                  },
                  validate: {
                    invalidChars: (value) =>
                      !value ||
                      /^[a-zA-ZąćęłńóśźżĆĄĘŁŃÓŚŹŻ\s'-]+$/.test(value) ||
                      "Imię zawiera niedozwolone znaki",
                  },
                }}
              />
              {errors.secondName && (
                <span className="validationError" role="alert">
                  <i className="fa fa-exclamation-circle" />
                  {errors.secondName.message}
                </span>
              )}
            </div>
          </div>

          <div className="row px-3 mt-4">
            <div className="form-group mt-1 mb-1">
              <Controller
                render={({ field }) => (
                  <>
                    <Autocomplete
                      ref={field.ref}
                      options={genders}
                      value={field.value ?? null}
                      onChange={(
                        event: any,
                        newValue: DictionaryCombo | null,
                      ) => {
                        field.onChange(newValue);
                      }}
                      getOptionLabel={(option) => option.name}
                      renderInput={(params) => (
                        <StyledTextField
                          {...params}
                          error={errors.gender != null}
                          name="gender"
                          label="Płeć"
                          variant="outlined"
                          size="small"
                        />
                      )}
                      noOptionsText={"Brak danych"}
                    />
                  </>
                )}
                name="gender"
                control={control}
                rules={{
                  required: "Płeć jest wymagana",
                }}
              />
              {errors.gender && (
                <span className="validationError" role="alert">
                  <i className="fa fa-exclamation-circle" />
                  Płeć jest wymagana
                </span>
              )}
            </div>
          </div>

          <div className="row px-3 mt-4">
            <div className="form-group mt-1 mb-1">
              <Controller
                render={({ field }) => (
                  <StyledTextField
                    ref={field.ref}
                    label="Miejsce urodzenia"
                    name="placeOfBirth"
                    variant="outlined"
                    value={field.value}
                    onChange={(event: any) => {
                      field.onChange(event.target.value);
                    }}
                    onBlur={(event: any) => {
                      if (event.target.value)
                        field.onChange(toTitleCase(event.target.value));
                    }}
                    error={errors.placeOfBirth != null}
                    inputProps={{
                      maxLength: 200,
                    }}
                  />
                )}
                name="placeOfBirth"
                control={control}
                rules={{
                  required: "Miejsce urodzenia jest wymagane",
                  maxLength: {
                    value: 200,
                    message: "Miejsce urodzenia jest za długie",
                  },
                  validate: {
                    invalidChars: (value) => {
                      const { invalidChars } = sanitizeValue(value);
                      if (invalidChars.length === 0) return true;

                      const unique = Array.from(new Set(invalidChars));
                      return `Podano nieprawidłowe znaki: ${unique.join(" ")}`;
                    },
                  },
                }}
              />
              {errors.placeOfBirth && (
                <span className="validationError" role="alert">
                  <i className="fa fa-exclamation-circle" />
                  {errors.placeOfBirth.message}
                </span>
              )}
            </div>
          </div>

          <div className="row px-3 mt-4">
            <div className="form-group mt-1 mb-1">
              <Controller
                render={({ field }) => (
                  <>
                    <Autocomplete
                      ref={field.ref}
                      options={
                        dictionary?.countries
                          .sort(compareDictionary)
                          .reduce(polandFirst, []) ?? []
                      }
                      value={field.value}
                      onChange={(
                        event: any,
                        newValue: DictionaryCombo | null,
                      ) => {
                        field.onChange(newValue);
                      }}
                      getOptionLabel={(option) => option.name}
                      isOptionEqualToValue={(option, value) =>
                        option.id === value.id
                      }
                      renderInput={(params) => (
                        <StyledTextField
                          {...params}
                          error={errors.countryOfBirth != null}
                          name="countryOfBirth"
                          label="Kraj urodzenia"
                          variant="outlined"
                          size="small"
                        />
                      )}
                      noOptionsText={"Brak danych"}
                    />
                  </>
                )}
                name="countryOfBirth"
                control={control}
                rules={{
                  required: "Kraj urodzenia jest wymagany",
                }}
              />
              {errors.countryOfBirth && (
                <span className="validationError" role="alert">
                  <i className="fa fa-exclamation-circle" />
                  Kraj urodzenia jest wymagany
                </span>
              )}
            </div>
          </div>

          <div className="row px-3 mt-4">
            <div className="form-group mt-1 mb-1">
              <Controller
                render={({ field }) => (
                  <>
                    <Autocomplete
                      ref={field.ref}
                      options={
                        dictionary?.citizenships
                          .sort(compareDictionary)
                          .reduce(polandFirst, []) ?? []
                      }
                      value={field.value}
                      onChange={(
                        event: any,
                        newValue: DictionaryCombo | null,
                      ) => {
                        field.onChange(newValue);
                      }}
                      getOptionLabel={(option) => option.name}
                      isOptionEqualToValue={(option, value) =>
                        option.id === value.id
                      }
                      renderInput={(params) => (
                        <StyledTextField
                          {...params}
                          error={errors.citizenship != null}
                          name="citizenship"
                          label="Kraj obywatelstwa"
                          variant="outlined"
                          size="small"
                        />
                      )}
                      noOptionsText={"Brak danych"}
                    />
                  </>
                )}
                name="citizenship"
                control={control}
                rules={{
                  required: "Kraj obywatelstwa jest wymagany",
                }}
              />
              {errors.citizenship && (
                <span className="validationError" role="alert">
                  <i className="fa fa-exclamation-circle" />
                  Kraj obywatelstwa jest wymagany
                </span>
              )}
            </div>
          </div>

          <div className="row px-3 mt-4">
            <div className="form-group mt-1 mb-1">
              <Controller
                render={({ field }) => (
                  <StyledTextField
                    ref={field.ref}
                    label="Nazwisko panieńskie matki"
                    name="mothersMaidenName"
                    variant="outlined"
                    value={field.value}
                    onChange={(event: any) => {
                      field.onChange(event.target.value);
                    }}
                    onBlur={(event: any) => {
                      if (event.target.value)
                        field.onChange(toTitleCase(event.target.value));
                    }}
                    error={errors.mothersMaidenName != null}
                    inputProps={{
                      maxLength: 200,
                    }}
                  />
                )}
                name="mothersMaidenName"
                control={control}
                rules={{
                  required: "Nazwisko panieńskie matki jest wymagane",
                  minLength: {
                    value: 2,
                    message: "Nazwisko panieńskie matki jest za krótkie",
                  },
                  maxLength: {
                    value: 200,
                    message: "Nazwisko panieńskie matki jest za długie",
                  },
                  validate: {
                    invalidChars: (value) =>
                      !value ||
                      /^[a-zA-ZąćęłńóśźżĆĄĘŁŃÓŚŹŻ\s'-]+$/.test(value) ||
                      "Nazwisko zawiera niedozwolone znaki",
                  },
                }}
              />
              {errors.mothersMaidenName && (
                <span className="validationError" role="alert">
                  <i className="fa fa-exclamation-circle" />
                  {errors.mothersMaidenName.message}
                </span>
              )}
            </div>
          </div>

          <hr className="hrMargin" />

          <p className="bold red section">Dowód osobisty</p>

          {IdentityTab("identification", identification)}

          <hr className="hrMargin" />
          <p className="bold red section">Adres zamieszkania</p>

          {AddressTab()}

          <div className="row px-3 mt-4 mb-3">
            <div
              onClick={setPostalAddressCheckbox}
              className="custom-control custom-checkbox"
            >
              <input
                {...register("otherPostalAddress")}
                id="otherPostalAddress"
                type="checkbox"
                className="custom-control-input"
              />
              <label className="custom-control-label">
                Chcę podać adres korespondencyjny (inny niż adres zamieszkania)
              </label>
            </div>
          </div>

          {showOtherPostalAddress ? (
            <>
              <hr className="hrMargin" />
              <p className="bold red section">Adres korespondencyjny</p>

              <div className="row px-3">
                <div className="form-group mt-1 mb-1">
                  <Controller
                    render={({ field }) => (
                      <>
                        <Autocomplete
                          ref={field.ref}
                          options={
                            dictionary?.countries
                              .sort(compareDictionary)
                              .reduce(polandFirst, []) ?? []
                          }
                          value={field.value}
                          onChange={(
                            event: any,
                            newValue: DictionaryCombo | null,
                          ) => {
                            field.onChange(newValue);
                            setCheckOtherAddressPostalCode(
                              newValue?.name?.toLocaleLowerCase() === "polska",
                            );
                            if (
                              newValue?.name?.toLocaleLowerCase() !== "polska"
                            ) {
                              clearErrors("address.postCode");
                            }
                          }}
                          getOptionLabel={(option) => option.name}
                          isOptionEqualToValue={(option: any, value: any) =>
                            option.id === value.id
                          }
                          renderInput={(params) => (
                            <StyledTextField
                              {...params}
                              error={errors.postalAddress?.country != null}
                              name="postalAddress.country"
                              label="Kraj"
                              variant="outlined"
                              size="small"
                            />
                          )}
                          noOptionsText={"Brak danych"}
                        />
                      </>
                    )}
                    name="postalAddress.country"
                    control={control}
                    rules={{
                      required: "Kraj jest wymagany",
                    }}
                  />
                  {errors.postalAddress?.country && (
                    <span className="validationError" role="alert">
                      <i className="fa fa-exclamation-circle" />
                      Kraj jest wymagany
                    </span>
                  )}
                </div>
              </div>

              <div className="row px-3 mt-4">
                <div className="form-group mt-1 mb-1">
                  <Controller
                    render={({ field }) =>
                      checkOtherAddressPostalCode ? (
                        <StyledTextField
                          ref={field.ref}
                          label="Kod pocztowy"
                          name="postalAddress.postCode"
                          variant="outlined"
                          error={Boolean(errors.address?.postCode)}
                          inputProps={{
                            maxLength: 9,
                          }}
                          InputProps={{
                            inputProps: {
                              name: "postalAddress.postCode",
                              value: field.value ?? "",
                              onBlur: field.onBlur,
                              onChange: field.onChange,
                              disableUnderline: true,
                              maskValue: "00-000",
                            },
                            inputComponent: TextMaskCustom as any,
                          }}
                        />
                      ) : (
                        <StyledTextField
                          ref={field.ref}
                          value={field.value}
                          onChange={(event: any) => {
                            field.onChange(event.target.value);
                            clearErrors("postalAddress.postCode");
                          }}
                          label="Kod pocztowy"
                          name="postalAddress.postCode"
                          variant="outlined"
                          error={errors.postalAddress?.postCode != null}
                          inputProps={{
                            maxLength: 9,
                          }}
                        />
                      )
                    }
                    name="postalAddress.postCode"
                    control={control}
                    rules={{
                      required: "Kod pocztowy jest wymagany",
                      maxLength: {
                        value: 9,
                        message: "Kod pocztowy jest za długi",
                      },
                      pattern:
                        showOtherPostalAddress && checkOtherAddressPostalCode
                          ? {
                            value: /\b[0-9][0-9]-[0-9][0-9][0-9]\b/,
                            message: "Nieprawidłowy kod pocztowy",
                          }
                          : undefined,
                    }}
                  />
                  {errors.postalAddress?.postCode && (
                    <span className="validationError" role="alert">
                      <i className="fa fa-exclamation-circle" />
                      {errors.postalAddress.postCode.message}
                    </span>
                  )}
                </div>
              </div>

              <div className="row px-3 mt-4">
                <div className="form-group mt-1 mb-1">
                  <Controller
                    render={({ field }) => (
                      <StyledTextField
                        ref={field.ref}
                        label="Miejscowość"
                        name="postalAddress.city"
                        variant="outlined"
                        value={field.value}
                        error={errors.postalAddress?.city != null}
                        onChange={(event: any) => {
                          field.onChange(event.target.value);
                        }}
                        onBlur={(event: any) => {
                          if (event.target.value)
                            field.onChange(toTitleCase(event.target.value));
                        }}
                        inputProps={{
                          maxLength: 200,
                        }}
                      />
                    )}
                    name="postalAddress.city"
                    control={control}
                    rules={{
                      required: "Miejscowość jest wymagana",
                      maxLength: {
                        value: 200,
                        message: "Miejscowość jest za długa",
                      },
                      validate: {
                        invalidChars: (value) => {
                          const { invalidChars } = sanitizeValue(value);
                          if (invalidChars.length === 0) return true;

                          const unique = Array.from(new Set(invalidChars));
                          return `Podano nieprawidłowe znaki: ${unique.join(
                            " ",
                          )}`;
                        },
                      },
                    }}
                  />
                  {errors.postalAddress?.city && (
                    <span className="validationError" role="alert">
                      <i className="fa fa-exclamation-circle" />
                      {errors.postalAddress.city.message}
                    </span>
                  )}
                </div>
              </div>

              <div className="row px-3 mt-4">
                <div className="form-group mt-1 mb-1">
                  <Controller
                    render={({ field }) => (
                      <StyledTextField
                        ref={field.ref}
                        label="Ulica"
                        name="postalAddress.street"
                        variant="outlined"
                        value={field.value}
                        error={errors.postalAddress?.street != null}
                        onChange={(event: any) => {
                          field.onChange(event.target.value);
                        }}
                        onBlur={(event: any) => {
                          if (event.target.value)
                            field.onChange(toTitleCase(event.target.value));
                        }}
                        inputProps={{
                          maxLength: 200,
                        }}
                      />
                    )}
                    name="postalAddress.street"
                    control={control}
                    rules={{
                      maxLength: {
                        value: 200,
                        message: "Ulica jest za długa",
                      },
                      validate: {
                        invalidChars: (value) => {
                          const { invalidChars } = sanitizeValue(value);
                          if (invalidChars.length === 0) return true;

                          const unique = Array.from(new Set(invalidChars));
                          return `Podano nieprawidłowe znaki: ${unique.join(
                            " ",
                          )}`;
                        },
                      },
                    }}
                  />
                  {errors.postalAddress?.street && (
                    <span className="validationError" role="alert">
                      <i className="fa fa-exclamation-circle" />
                      {errors.postalAddress.street.message}
                    </span>
                  )}
                </div>
              </div>

              <div className="row px-3 mt-4">
                <div className="form-group mt-1 mb-1">
                  <Controller
                    render={({ field }) => (
                      <StyledTextField
                        ref={field.ref}
                        label="Numer domu"
                        name="postalAddress.house"
                        variant="outlined"
                        value={field.value}
                        error={errors.postalAddress?.house != null}
                        onChange={(event: any) => {
                          field.onChange(event.target.value);
                        }}
                        inputProps={{
                          maxLength: 10,
                        }}
                      />
                    )}
                    name="postalAddress.house"
                    control={control}
                    rules={{
                      required: "Numer domu jest wymagany",
                      maxLength: {
                        value: 10,
                        message: "Numer domu jest za długi",
                      },
                      validate: {
                        invalidChars: (value) => {
                          const { invalidChars } = sanitizeValue(value);
                          if (invalidChars.length === 0) return true;

                          const unique = Array.from(new Set(invalidChars));
                          return `Podano nieprawidłowe znaki: ${unique.join(
                            " ",
                          )}`;
                        },
                      },
                    }}
                  />
                  {errors.postalAddress?.house && (
                    <span className="validationError" role="alert">
                      <i className="fa fa-exclamation-circle" />
                      {errors.postalAddress.house.message}
                    </span>
                  )}
                </div>
              </div>

              <div className="row px-3 mt-4">
                <div className="form-group mt-1 mb-1">
                  <Controller
                    render={({ field }) => (
                      <StyledTextField
                        ref={field.ref}
                        label="Numer mieszkania"
                        name="postalAddress.apartment"
                        variant="outlined"
                        value={field.value}
                        onChange={(event: any) => {
                          field.onChange(event.target.value);
                        }}
                        inputProps={{
                          maxLength: 10,
                        }}
                      />
                    )}
                    name="postalAddress.apartment"
                    control={control}
                    rules={{
                      maxLength: {
                        value: 10,
                        message: "Numer mieszkania jest za długi",
                      },
                      validate: {
                        invalidChars: (value) => {
                          const { invalidChars } = sanitizeValue(value);
                          if (invalidChars.length === 0) return true;

                          const unique = Array.from(new Set(invalidChars));
                          return `Podano nieprawidłowe znaki: ${unique.join(
                            " ",
                          )}`;
                        },
                      },
                    }}
                  />
                </div>
              </div>
            </>
          ) : null}

          <div className="row px-3 mt-4">
            <div className="form-group mt-1 mb-1">
              <input
                {...register("identificationPhotos.mainSide", {
                  required: "Plik jest wymagany",
                  validate: {
                    file: (value) => {
                      return value && value.length > 0;
                    },
                  },
                })}
                onChange={(e) => {
                  if (e && e.target && e.target.files && e.target.files[0]) {
                    const file = FileListItems([e.target.files[0]]);

                    if (file instanceof FileList) {
                      setValue(
                        "identificationPhotos.mainSide",
                        (file as FileList) ?? undefined,
                        {
                          shouldValidate: true,
                        },
                      );
                      setFile1Error(undefined);
                    } else {
                      setFile1Error(file as string);
                      setFileName1("");
                      setValue("identificationPhotos.mainSide", undefined, {
                        shouldValidate: true,
                      });
                    }
                    setFileName1(e.target.files[0]?.name ?? "");
                  }
                }}
                accept="image/jpeg,image/png,application/pdf"
                name="identificationPhotos.mainSide"
                ref={inputReference1}
                type="file"
                hidden
              />
              <StyledTextField
                label={`${identityType ?? "Dokument tożsamości"
                  } - strona ze zdjęciem`}
                disabled
                variant="outlined"
                value={fileName1}
                error={
                  file1Error != null ||
                  errors.identificationPhotos?.mainSide != null
                }
                InputLabelProps={{
                  shrink: fileName1 != null && fileName1.length > 0,
                }}
              />
              {fileName1 ? (
                <input
                  onClick={(event: any) => {
                    event.preventDefault();
                    if (inputReference1 != null) {
                      inputReference1?.current?.click();
                    }
                  }}
                  className="reAttachmentBtn mt-3"
                  type="submit"
                  value="Zmień plik"
                />
              ) : (
                <input
                  onClick={(event: any) => {
                    event.preventDefault();
                    if (inputReference1 != null) {
                      inputReference1?.current?.click();
                    }
                  }}
                  className="attachmentBtn mt-3"
                  type="submit"
                  value="Dołącz plik"
                />
              )}
            </div>
          </div>
          {file1Error ? (
            <p className="validationError" role="alert">
              <i className="fa fa-exclamation-circle" />
              {file1Error}
            </p>
          ) : (
            errors.identificationPhotos?.mainSide && (
              <span className="validationError" role="alert">
                <i className="fa fa-exclamation-circle" />
                Plik jest wymagany
              </span>
            )
          )}
          {identityType?.toLocaleLowerCase() !== "paszport" ? (
            <>
              <div className="row px-3 mt-4">
                <div className="form-group mt-1 mb-1">
                  <input
                    {...register("identificationPhotos.backSide", {
                      required: "Plik jest wymagany",
                      validate: {
                        file: (value) => {
                          return value && value.length > 0;
                        },
                      },
                    })}
                    onChange={(e) => {
                      if (
                        e &&
                        e.target &&
                        e.target.files &&
                        e.target.files[0]
                      ) {
                        const file = FileListItems([e.target.files[0]]);

                        if (file instanceof FileList) {
                          setValue(
                            "identificationPhotos.backSide",
                            (file as FileList) ?? undefined,
                            {
                              shouldValidate: true,
                            },
                          );
                          setFile2Error(undefined);
                        } else {
                          setFile2Error(file as string);
                          setFileName2(undefined);
                          setValue("identificationPhotos.backSide", undefined, {
                            shouldValidate: true,
                          });
                        }
                        setFileName2(e.target.files[0]?.name ?? "");
                      }
                    }}
                    accept="image/jpeg,image/png,application/pdf"
                    name="identificationPhotos.backSide"
                    ref={inputReference2}
                    type="file"
                    hidden
                  />
                  <StyledTextField
                    label={`${identityType ?? "Dokument tożsamości"
                      }  - druga strona`}
                    disabled
                    variant="outlined"
                    value={fileName2}
                    error={
                      file2Error != null ||
                      errors.identificationPhotos?.backSide != null
                    }
                    InputLabelProps={{
                      shrink: fileName2 != null && fileName2.length > 0,
                    }}
                  />
                  {fileName2 ? (
                    <input
                      onClick={(event: any) => {
                        event.preventDefault();
                        if (inputReference2 != null) {
                          inputReference2?.current?.click();
                        }
                      }}
                      className="reAttachmentBtn mt-3"
                      type="submit"
                      value="Zmień plik"
                    />
                  ) : (
                    <input
                      onClick={(event: any) => {
                        event.preventDefault();
                        if (inputReference2 != null) {
                          inputReference2?.current?.click();
                        }
                      }}
                      className="attachmentBtn mt-3"
                      type="submit"
                      value="Dołącz plik"
                    />
                  )}
                </div>
              </div>
              {file2Error ? (
                <p className="validationError" role="alert">
                  <i className="fa fa-exclamation-circle" />
                  {file2Error}
                </p>
              ) : (
                errors.identificationPhotos?.backSide && (
                  <span className="validationError" role="alert">
                    <i className="fa fa-exclamation-circle" />
                    Plik jest wymagany
                  </span>
                )
              )}
            </>
          ) : null}

          <div className="row px-3 mb-3 mt-3">
            <div className="col-sm-12 code">
              <div className="mr-12  float-right">
                <button
                  onClick={() => {
                    setStep(1);
                    if (context.setStep2) {
                      context.setStep2(undefined);
                    }
                    if (context.setPPP) {
                      context.setPPP(undefined);
                    }
                  }}
                  className="backBtn"
                >
                  Cofnij
                </button>
                <input className="nextBtn" type="submit" value="Dalej" />
              </div>
            </div>
          </div>
        </form>
        {loading && <LoadingPanel />}
      </ThemeProvider>
    </>
  );
};

export { Step2Form };
